/**
 * Home-Eingabefeld (Vereinfachung S1, 2026-07-08 — Claude.ai-Muster).
 *
 * EIN zentrales Feld ist das Primärelement der Startseite. Enter = Chat-Frage
 * (bestehender Frage-Flow: POST /chats → POST /chats/{id}/messages → /chat/{id},
 * exakt wie Chat.tsx bei leerem Chat). Kein neuer Endpoint.
 *
 * Vor dem Tippen sichtbar: Feld + Senden + Projekt-Chip. Erst wenn Text getippt
 * ist, erscheint „Als Notiz speichern" (bestehender Notiz-Save, POST /notes mit
 * Inline-#tags / @kategorie). Kein Kategorie-Dropdown, kein „Quick-Note"-Label.
 */
import { FormEvent, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Send, Paperclip, Check, Loader2, Settings2 } from "lucide-react";
import { api } from "../api";
import { Button, projectDisplayName } from "../ui";
import { useToast } from "../ui/toast";
import { useT } from "../i18n";
import {
  useDocAttachment,
  AttachInput,
  AttachmentChip,
  ATTACH_DOC_EVENT,
} from "./useDocAttachment";
import ResearchModePicker, { useResearchMode } from "./ResearchModePicker";

interface Category {
  id: string;
  name: string;
}
interface LocalProject {
  id: string;
  name: string;
}

/** Ziel-Projekt der Home-Eingabe. "" = „Allgemein" (kein Projekt-Tag). */
const LAST_PROJECT_KEY = "sb_last_project";

/** Inline-Parse analog QuickCapture: #tags + @kategorie aus dem Text ziehen. */
function parseInline(body: string): { tags: string[]; category: string | null } {
  const tags: string[] = [];
  for (const m of body.matchAll(/(?:^|\s)#([\w-]{1,30})/g)) {
    tags.push(m[1].toLowerCase());
  }
  const catMatch = body.match(/(?:^|\s)@([\w-]{1,30})/);
  return { tags, category: catMatch ? catMatch[1] : null };
}

export default function UnifiedComposer() {
  const toast = useToast();
  const t = useT();
  const navigate = useNavigate();
  const location = useLocation();
  const taRef = useRef<HTMLTextAreaElement>(null);
  const [text, setText] = useState("");
  const [asking, setAsking] = useState(false);
  const [noteBusy, setNoteBusy] = useState(false);

  const [categories, setCategories] = useState<Category[]>([]);
  const [projects, setProjects] = useState<LocalProject[]>([]);
  const [projectId, setProjectId] = useState<string>(
    () => localStorage.getItem(LAST_PROJECT_KEY) ?? ""
  );
  const [optsOpen, setOptsOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [researchMode, setResearchMode] = useResearchMode();

  // Anhang-Flow: Upload landet im aktuellen Ziel-Projekt (bei "" → Backend-
  // Fallback „Allgemein"). Senden legt den Chat im PROJEKT des Docs an.
  const attach = useDocAttachment(projectId || null);

  useEffect(() => {
    localStorage.setItem(LAST_PROJECT_KEY, projectId);
  }, [projectId]);

  // Home-Chip „Dokument hochladen" ist ein Link auf `/?attach=doc`. Param
  // erkannt → Datei-Dialog öffnen und Param wieder entfernen (damit ein
  // erneuter Klick denselben Weg triggert und ein Reload nicht neu öffnet).
  useEffect(() => {
    if (new URLSearchParams(location.search).has("attach")) {
      attach.openDialog();
      navigate("/", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // Alternativ: externer Trigger via CustomEvent (Ref-frei), z. B. aus anderen
  // Komponenten. Harmlos, wenn niemand das Event feuert.
  useEffect(() => {
    const open = () => attach.openDialog();
    window.addEventListener(ATTACH_DOC_EVENT, open);
    return () => window.removeEventListener(ATTACH_DOC_EVENT, open);
  }, [attach.openDialog]);

  useEffect(() => {
    api.get<Category[]>("/categories").then(setCategories).catch(() => {});
    api.get<LocalProject[]>("/projects").then(setProjects).catch(() => {});
  }, []);

  // „Allgemein" ist hier reine ANZEIGE für „kein Projekt gewählt"; an den
  // Server geht die leere Projekt-Id, nicht dieser Text.
  const projectName =
    projects.find((p) => p.id === projectId)?.name ?? t.shared.composer.allgemein;

  // Enter = Frage → Chat. Legt nur den Chat an (mit Ziel-Projekt) und navigiert
  // SOFORT zu /chat/{id} — die eigentliche Frage reicht Chat.tsx als History-State
  // `pendingQuestion` durch und sendet sie dort über den normalen Sende-/Stream-
  // Flow. So blockiert die Home-Eingabe nicht mehr auf die komplette LLM-Antwort.
  const ask = async (e?: FormEvent) => {
    e?.preventDefault();
    const content = text.trim();
    const doc = attach.attachment?.doc ?? null;
    // Senden, sobald Text ODER ein hochgeladenes Dokument da ist. Während der
    // Upload noch läuft (keine Doc-Id) blockieren wir.
    if (attach.uploading || (!content && !doc) || asking) return;
    setAsking(true);
    try {
      // Mit Dokument = Chat im Projekt des Docs (repliziert „Mit Dokument
      // unterhalten", DocumentDetail.tsx:136). Titel nur setzen, wenn keine
      // Frage getippt ist — sonst darf die Auto-Betitelung die Frage nutzen.
      // `Über: …` ist der gespeicherte Chat-TITEL (Inhalt, kein Etikett) und
      // bleibt deshalb deutsch: die Chat-Oberfläche ist als Ganzes noch nicht
      // umgestellt (docs/i18n.md §5). NICHT mehr, weil die Auto-Betitelung
      // deutsch wäre — die folgt seit 680ee5e der Sprach-Einstellung.
      const body: Record<string, unknown> = doc
        ? {
            project_id: doc.project_id,
            ...(content ? {} : { title: `Über: ${doc.title}` }),
          }
        : projectId
          ? { project_id: projectId }
          : {};
      const chat = await api.post<{ id: string }>("/chats", body);
      setText("");
      attach.clear();
      navigate(`/chat/${chat.id}`, {
        state: content
          ? { pendingQuestion: content, pendingResearchMode: researchMode }
          : {},
      });
    } catch (err) {
      toast.err(t.shared.composer.frageFehlgeschlagen, (err as Error).message);
    } finally {
      setAsking(false);
    }
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) attach.onFile(f);
  };

  // Bestehender Notiz-Save (POST /notes) inkl. Inline-#tags/@kategorie.
  const saveNote = async () => {
    const body = text.trim();
    if (!body || noteBusy) return;
    setNoteBusy(true);
    try {
      const parsed = parseInline(body);
      let category_id: string | null = null;
      if (parsed.category) {
        const found = categories.find(
          (c) => c.name.toLowerCase() === parsed.category!.toLowerCase()
        );
        if (found) category_id = found.id;
        else {
          try {
            const created = await api.post<Category>("/categories", {
              name: parsed.category,
            });
            category_id = created.id;
            setCategories((s) => [...s, created]);
          } catch {
            /* Duplikat o. Ä. — Notiz ohne Kategorie speichern */
          }
        }
      }
      const payload: Record<string, unknown> = { body_md: body };
      if (parsed.tags.length) payload.tags = parsed.tags;
      if (category_id) payload.category_id = category_id;
      if (projectId) payload.project_id = projectId;
      await api.post("/notes", payload);
      toast.ok(t.shared.composer.notizGespeichert);
      setText("");
      taRef.current?.focus();
    } catch (err) {
      toast.err(t.common.speichernFehlgeschlagen, (err as Error).message);
    } finally {
      setNoteBusy(false);
    }
  };

  const hasText = text.trim().length > 0;
  const canSend = !attach.uploading && (hasText || attach.hasDoc);
  // Progressive Disclosure (I-06): Sekundär-Controls (Projekt, Anhang,
  // Recherche-Tiefe) liegen hinter EINEM ⚙-Optionen-Trigger, der erst bei
  // Fokus/Text erscheint. Idle = nur Feld + Senden sichtbar.
  const showTrigger = focused || hasText || optsOpen;

  return (
    <form
      onSubmit={ask}
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={(e) => {
        // Nur zurücksetzen, wenn der Cursor die Box wirklich verlässt.
        if (!e.currentTarget.contains(e.relatedTarget as Node)) setDragOver(false);
      }}
      onDrop={onDrop}
      className={
        "bg-surface border rounded-2xl shadow-sm hover:shadow-md focus-within:shadow-md transition " +
        (dragOver
          ? "border-accent-300 bg-accent-50 dark:bg-accent-900/10"
          : "border-border focus-within:border-accent-300")
      }
    >
      <AttachInput api={attach} />
      <textarea
        ref={taRef}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={t.shared.composer.platzhalter}
        aria-label={t.shared.composer.feldAria}
        rows={3}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            ask();
          }
        }}
        className="w-full px-4 py-3.5 text-[15px] bg-transparent focus:outline-none resize-none placeholder:text-text-3"
      />
      {attach.attachment && (
        <div className="px-3 pb-1">
          <AttachmentChip attachment={attach.attachment} onRemove={attach.clear} />
        </div>
      )}
      <div className="flex items-center gap-2 px-3 py-2.5 border-t border-border">
        {/* ⚙ Optionen — EIN Trigger bündelt alle Sekundär-Controls (Projekt,
            Recherche-Tiefe, Anhang). Erscheint erst bei Fokus/Text, damit im
            Ausgangszustand nur Feld + Senden sichtbar sind (I-06). */}
        {showTrigger && (
        <div className="relative">
          <button
            type="button"
            onClick={() => setOptsOpen((o) => !o)}
            className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs text-text-2 hover:bg-surface-3 transition-colors"
            title={t.shared.composer.optionen}
            aria-haspopup="menu"
            aria-expanded={optsOpen}
          >
            <Settings2 className="w-3.5 h-3.5 text-text-3" />
            <span className="max-w-[10rem] truncate">{projectDisplayName(projectName)}</span>
          </button>
          {optsOpen && (
            <>
              <button
                type="button"
                aria-label={t.shared.composer.optionenSchliessen}
                className="fixed inset-0 z-30 cursor-default"
                onClick={() => setOptsOpen(false)}
              />
              <div className="absolute z-40 top-full mt-1.5 left-0 w-72 max-w-[calc(100vw-2rem)] bg-surface border border-border rounded-xl shadow-lg py-1">
                {/* Ziel-Projekt. */}
                <div className="px-3 pt-1.5 pb-1 text-[11px] font-medium text-text-3 uppercase tracking-wide">
                  {t.shared.composer.zielProjekt}
                </div>
                <div className="max-h-56 overflow-y-auto">
                  <ProjectMenuItem
                    label={t.shared.composer.allgemein}
                    active={projectId === ""}
                    onClick={() => setProjectId("")}
                  />
                  {projects.map((p) => (
                    <ProjectMenuItem
                      key={p.id}
                      label={projectDisplayName(p.name)}
                      active={projectId === p.id}
                      onClick={() => setProjectId(p.id)}
                    />
                  ))}
                </div>
                {/* Recherche-Tiefe (off/normal/deep) — reicht die Wahl an den
                    Chat weiter (pendingResearchMode). */}
                <div className="mt-1 mx-3 border-t border-border" />
                <div className="px-3 pt-2">
                  <ResearchModePicker
                    mode={researchMode}
                    setMode={setResearchMode}
                    variant="full"
                  />
                </div>
                {/* 📎 → Datei-Dialog + Hintergrund-Upload. */}
                <div className="mt-2 mx-3 border-t border-border" />
                <button
                  type="button"
                  onClick={() => {
                    setOptsOpen(false);
                    attach.openDialog();
                  }}
                  className="w-full flex items-center gap-2.5 px-3 py-2 text-left text-sm text-text hover:bg-surface-2 transition-colors"
                >
                  {attach.uploading ? (
                    <Loader2 className="w-4 h-4 animate-spin text-accent-500 shrink-0" />
                  ) : (
                    <Paperclip className="w-4 h-4 text-text-3 shrink-0" />
                  )}
                  <span>{t.shared.composer.dokumentAnhaengen}</span>
                  <span className="ml-auto text-xs text-text-3">
                    {t.shared.composer.dateiTypen}
                  </span>
                </button>
              </div>
            </>
          )}
        </div>
        )}

        <div className="ml-auto flex items-center gap-2">
          {hasText && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              loading={noteBusy}
              onClick={saveNote}
            >
              {t.shared.composer.alsNotizSpeichern}
            </Button>
          )}
          <Button
            type="submit"
            variant="primary"
            size="sm"
            loading={asking}
            disabled={!canSend}
            icon={<Send className="w-4 h-4" />}
          >
            {t.shared.composer.senden}
          </Button>
        </div>
      </div>
    </form>
  );
}

function ProjectMenuItem({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-left text-text hover:bg-surface-2 transition-colors"
    >
      <span className="flex-1 truncate">{label}</span>
      {active && <Check className="w-3.5 h-3.5 text-accent-600 shrink-0" />}
    </button>
  );
}
